<template>
  <div class="cn-list">
    <transition-group
      tag="ul"
      class="cn-list__list"
      enter-active-class="transform duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
    >
      <li
        v-for="(item, i) in items"
        v-show="isItemShown(i)"
        :key="item"
        class="cn-list__item"
      >
        <UiIcon name="check" />
        <span v-html="item" />
      </li>
    </transition-group>
    <button
      v-if="collapsiable && items.length > shownItemsCount"
      class="cn-list__toggle"
      :class="{ 'cn-list__toggle--hide': !isCollapse }"
      @click="toggleCollapse"
    >
      <UiIcon name="arrow-down" />
      <span v-if="isCollapse">{{ $t('show_more') }}</span>
      <span v-else>{{ $t('hide') }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useScrollTrigger } from '@/composables';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  items: string[];
  collapsiable?: boolean;
  shownItemsCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  collapsiable: false,
  shownItemsCount: 6,
});

const { refresh } = useScrollTrigger();
const isCollapse = ref(true);
const toggleCollapse = () => {
  isCollapse.value = !isCollapse.value;
};

const isItemShown = (index: number): boolean => {
  if (!props.collapsiable) return true;
  if (
    (props.items.length || Object.entries(props.items).length) <=
    props.shownItemsCount
  )
    return true;
  if (index < props.shownItemsCount || !isCollapse.value) return true;
  return false;
};

watch(isCollapse, () => {
  setTimeout(() => {
    refresh();
  }, 200);
});
</script>

<style lang="scss">
.cn-list {
  --ab-canyon-list-color: var(--ab-canyon-color-neutral-dark);
  --ab-canyon-list-link-color: var(--ab-canyon-color-accent);
  --ab-canyon-list-toggle-color: var(--ab-canyon-color-accent);

  padding: var(--size-base) 0;
  &__list {
    color: var(--ab-canyon-list-color);
    display: flex;
    flex-direction: column;
    row-gap: calc(var(--size-base) / 2);

    @media (min-width: 640px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }

    li {
      span {
        ::v-deep a {
          color: var(--ab-canyon-list-link-color);
          transition: filter 0.2s;
          &:hover {
            filter: brightness(0.9);
          }
        }
      }
      a {
        color: var(--ab-canyon-list-link-color);
        transition: filter 0.2s;
        &:hover {
          filter: brightness(0.9);
        }
      }
    }
  }
  &__item {
    display: flex;
    align-items: center;
    column-gap: var(--size-base);
    > .cn-icon {
      width: var(--size-base);
      height: var(--size-base);
    }
  }
  &__toggle {
    margin-top: var(--size-xl);
    display: inline-flex;
    align-items: center;
    column-gap: calc(var(--size-base) / 2);
    font-size: 1.125rem;
    line-height: 1.75rem;
    color: var(--ab-canyon-list-toggle-color);
    .cn-icon {
      transition-property: transform;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 150ms;
      width: var(--size-base);
      height: var(--size-base);
    }
    &--hide {
      .cn-icon {
        transform: rotate(180deg);
      }
    }
  }
}
</style>
